import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { Bar } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend
} from 'chart.js';
import './SessionsChart.css';
import tooltipTexts from '../../utils/tooltipTexts';

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend
);

const SessionsChart = ({ vehicleId }) => {
  const [isLoading, setIsLoading] = useState(true);
  const [chartData, setChartData] = useState(null);
  const [viewType, setViewType] = useState('session'); // 'session' or 'assessment'

  useEffect(() => {
    fetchData();
  }, [viewType, vehicleId]);

  const getBackgroundColor = (category) => {
    switch(category) {
      case 'Excellent': return '#00aa00';
      case 'Average': return '#ff9966';
      case 'Good': return '#ffff00';
      case 'Poor': return '#ff0000';
      default: return '#999999';
    }
  };

  // Function to generate a dataset for each category
  const createDataset = (category, labels, data) => {
    const color = getBackgroundColor(category);
    return {
      label: category,
      data: labels.map(label => {
        // Ensure data is properly formatted and return 0 if no value exists
        if (data[label] && data[label][category] !== undefined) {
          return parseFloat(data[label][category] || '0');
        }
        return 0;
      }),
      backgroundColor: color,
      borderColor: color,
      borderWidth: 2,
      hoverBackgroundColor: color,
      barPercentage: 0.6,
      categoryPercentage: 0.7,
      minBarLength: 3 // Ensure minimum bar length for visibility
    };
  };

  const fetchData = async () => {
    setIsLoading(true);
    try {
      const endpoint = viewType === 'session' 
        ? '/api/score-distribution'
        : '/api/assessment-distribution';
        
      const url = vehicleId
        ? `http://eframeehs.in:3001${endpoint}?vehicleId=${vehicleId}`
        : `http://eframeehs.in:3001${endpoint}`;
      
      const response = await fetch(url);
      if (!response.ok) throw new Error('Failed to fetch data');
      const data = await response.json();
      
      // Transform API data into chart format
      const labels = Object.keys(data);
      const categories = ['Excellent', 'Good', 'Average' , 'Poor'];
      
      // Create datasets for each category
      const datasets = categories.map(category => createDataset(category, labels, data));
      
      setChartData({
        labels: labels,
        datasets: datasets
      });
    } catch (error) {
      console.error('Error loading chart data:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const options = {
    responsive: true,
    maintainAspectRatio: false,
    scales: {
      x: {
        stacked: false,
        barPercentage: 0.6,
        categoryPercentage: 0.7,
        grid: {
          display: false
        },
        ticks: {
          font: {
            weight: 'bold'
          },
          color: '#000000'
        }
      },
      y: {
        stacked: false,
        beginAtZero: true,
        max: 100,
        border: {
          display: true,
          width: 2,
          color: '#e0e0e0'
        },
        grid: {
          color: (context) => {
            // Make the zero line more prominent
            if (context.tick.value === 0) {
              return '#999999';
            }
            return '#e0e0e0';
          },
          lineWidth: (context) => {
            // Make the zero line thicker
            if (context.tick.value === 0) {
              return 2;
            }
            return 1;
          }
        },
        ticks: {
          callback: function(value) {
            return value + '%';
          },
          font: {
            weight: 'bold'
          },
          color: '#000000'
        }
      }
    },
    plugins: {
      legend: {
        position: 'bottom',
        labels: {
          usePointStyle: true,
          pointStyle: 'rect',
          boxWidth: 15,
          boxHeight: 15,
          padding: 20,
          font: {
            weight: 'bold'
          },
          color: '#000000'
        }
      },
      title: {
        display: false
      },
      tooltip: {
        callbacks: {
          label: function(context) {
            const value = context.raw || 0;
            return `${context.dataset.label}: ${value}%`;
          }
        }
      }
    },
    elements: {
      bar: {
        borderWidth: 2,
        borderColor: function(context) {
          return context.dataset.backgroundColor;
        },
        minBarLength: 3 // Ensure minimum bar length for visibility
      }
    }
  };

  const handleViewChange = (e) => {
    setViewType(e.target.value);
  };

  if (isLoading) {
    return (
      <div className="chart-container" style={{ height: '350px' }}>
        <div className="chart-header">
          <h3>Category distribution by user Score
            <span className="info-tooltip">
              <span className="info-icon">i</span>
              <span className="tooltip-text">{tooltipTexts.scoreDistribution}</span>
            </span>
          </h3>
          <select value={viewType} onChange={handleViewChange} className="view-selector">
            <option value="session">Session-wise</option>
            <option value="assessment">Assessment-wise</option>
          </select>
        </div>
        <div className="loading-container">
          Loading chart data...
        </div>
      </div>
    );
  }

  if (!chartData) {
    return (
      <div className="chart-container" style={{ height: '350px' }}>
        <div className="chart-header">
          <h3>Category distribution by user Score
            <span className="info-tooltip">
              <span className="info-icon">i</span>
              <span className="tooltip-text">{tooltipTexts.scoreDistribution}</span>
            </span>
          </h3>
          <select value={viewType} onChange={handleViewChange} className="view-selector">
            <option value="session">Session-wise</option>
            <option value="assessment">Assessment-wise</option>
          </select>
        </div>
        <div className="loading-container">
          No data available
        </div>
      </div>
    );
  }

  return (
    <div className="chart-container" style={{ height: '350px' }}>
      <div className="chart-header">
        <h3>Category distribution by user Score
          <span className="info-tooltip">
            <span className="info-icon">i</span>
            <span className="tooltip-text">{tooltipTexts.scoreDistribution}</span>
          </span>
        </h3>
        <select value={viewType} onChange={handleViewChange} className="view-selector">
          <option value="session">Session-wise</option>
          <option value="assessment">Assessment-wise</option>
        </select>
      </div>
      <div style={{ position: 'relative', height: '280px', flex: 1 }}>
        <Bar data={chartData} options={options} />
      </div>
    </div>
  );
};

SessionsChart.propTypes = {
  vehicleId: PropTypes.string
};

export default SessionsChart;
